import {
  Checkbox,
  Input,
  InputGroup,
  InputRightElement,
  NumberInput,
  NumberInputField,
} from '@chakra-ui/react';
import { KeyTermFieldFragment } from '@client/graphql/__generated__/types';
import { FieldType, InputFormatType } from '@common/enums';
import dayjs from 'dayjs';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { CurrencyInput } from '~/common/components/Inputs/CurrencyInput';
import { formatPercentageInputValue } from '~/common/utils/percentage';

type FieldValue = string | number | boolean | null | undefined;

const parseNumericFieldValue = (rawValue: FieldValue): number | null => {
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return null;
  }

  const numericValue =
    typeof rawValue === 'number' ? rawValue : Number(rawValue);

  return Number.isFinite(numericValue) ? numericValue : null;
};

interface EditableKeyTermFieldProps {
  field: KeyTermFieldFragment;
  value: FieldValue;
  size?: 'xs' | 'sm' | 'md';
  onChange: (value: FieldValue) => void;
  isDisabled?: boolean;
}

export const EditableKeyTermField: FC<EditableKeyTermFieldProps> = ({
  field,
  value,
  size = 'md',
  onChange,
  isDisabled = false,
}) => {
  const fieldType = field.fieldType as FieldType;
  const formatType = field.fieldFormat?.type as InputFormatType | undefined;
  const [percentageInputValue, setPercentageInputValue] = useState<string>(
    () => {
      if (formatType !== InputFormatType.PERCENTAGE) {
        return '';
      }

      const precision =
        typeof field.fieldFormat?.options?.precision === 'number'
          ? field.fieldFormat.options.precision
          : 2;
      const numericValue = parseNumericFieldValue(value);

      if (numericValue === null) {
        return '';
      }

      return formatPercentageInputValue(numericValue, precision);
    }
  );

  const resolvePrecision = (): number | undefined => {
    const precision = field.fieldFormat?.options?.precision;

    return typeof precision === 'number' ? precision : undefined;
  };

  useEffect(() => {
    if (formatType !== InputFormatType.PERCENTAGE) {
      return;
    }

    const precision =
      typeof field.fieldFormat?.options?.precision === 'number'
        ? field.fieldFormat.options.precision
        : 2;
    const numericValue = parseNumericFieldValue(value);
    const nextValue =
      numericValue === null
        ? ''
        : formatPercentageInputValue(numericValue, precision);

    setPercentageInputValue((current) =>
      current === nextValue ? current : nextValue
    );
  }, [value, formatType, field.fieldFormat?.options?.precision]);

  // Handle different field types
  switch (fieldType) {
    case FieldType.CHECKBOX:
      return (
        <Checkbox
          isChecked={Boolean(value)}
          isDisabled={isDisabled}
          onChange={(e) => onChange(e.target.checked)}
        />
      );

    case FieldType.NUMBER:
      // Check if it's a currency field based on format
      if (formatType === InputFormatType.CURRENCY) {
        return (
          <CurrencyInput
            fontSize="sm"
            isDisabled={isDisabled}
            size={size}
            value={
              value === null || value === undefined ? undefined : Number(value)
            }
            onValueChange={(val) =>
              onChange(
                val === null || val === undefined ? undefined : Number(val)
              )
            }
          />
        );
      }

      if (formatType === InputFormatType.PERCENTAGE) {
        const precision = resolvePrecision() ?? 2;

        const handlePercentageChange = (
          event: ChangeEvent<HTMLInputElement>
        ) => {
          const rawValue = event.target.value;
          setPercentageInputValue(rawValue);
          const trimmedValue = rawValue.trim();

          if (trimmedValue === '') {
            onChange(null);

            return;
          }

          if (trimmedValue.endsWith('.')) {
            return;
          }

          const parsedValue = Number(trimmedValue);

          if (Number.isNaN(parsedValue)) {
            return;
          }

          onChange(parsedValue);
        };

        return (
          <InputGroup size={size}>
            <Input
              fontSize="sm"
              inputMode="decimal"
              isDisabled={isDisabled}
              size={size}
              type="text"
              value={percentageInputValue}
              onChange={handlePercentageChange}
            />
            <InputRightElement pointerEvents="none" pr={3}>
              %
            </InputRightElement>
          </InputGroup>
        );
      }

      // Regular number input
      return (
        <NumberInput
          isDisabled={isDisabled}
          size={size}
          value={value !== null && value !== undefined ? String(value) : ''}
          onChange={(valueString, valueNumber) =>
            onChange(isNaN(valueNumber) ? null : valueNumber)
          }
        >
          <NumberInputField fontSize="sm" py={2} />
        </NumberInput>
      );

    case FieldType.DATE:
    case FieldType.SIGNED_DATE:
      // Convert to YYYY-MM-DD format for HTML date input
      const dateValue =
        value && typeof value === 'string' && dayjs(value).isValid()
          ? dayjs(value).format('YYYY-MM-DD')
          : '';

      return (
        <Input
          fontSize="sm"
          isDisabled={isDisabled}
          size={size}
          type="date"
          value={dateValue}
          onChange={(e) => {
            const newDate = e.target.value;
            // Convert back to ISO string or keep as YYYY-MM-DD
            onChange(newDate ? dayjs(newDate).format('YYYY-MM-DD') : null);
          }}
        />
      );

    case FieldType.TEXT:
    case FieldType.TEXTAREA:
    case FieldType.SELECT:
    case FieldType.RADIO:
    default:
      // Default to text input
      return (
        <Input
          fontSize="sm"
          isDisabled={isDisabled}
          size={size}
          value={value !== null && value !== undefined ? String(value) : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
};
